import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Bike, Trash2, Pencil, ImagePlus, X, Zap, Star, MessageSquare, AlertTriangle } from "lucide-react";

const SPECS = [
  ["motor", "Motor"], ["baterie", "Baterie"], ["viteza", "Viteza maxima"], ["autonomie", "Autonomie"],
  ["greutate", "Greutate"], ["incarcare_max", "Incarcare maxima"], ["roti", "Roti"], ["frane", "Frane"],
  ["ip", "Rezistenta apa (IP)"], ["timp_incarcare", "Timp incarcare"], ["urcare", "Capacitate urcare"],
  ["suspensie", "Suspensie"], ["iluminare", "Iluminare LED"], ["aplicatie", "Aplicatie mobila"],
  ["dimensiuni", "Dimensiuni"], ["cadru", "Cadru"],
];

const emptyForm = { name: "", price: "", old_price: "", stock: "", category: "trotineta", short_desc: "", description: "", specs: {}, featured: false, active: true, compatible_models: [] };
const CATEGORIES = { trotineta: "Trotinetă", piesa: "Piesă", accesoriu: "Accesoriu" };
const fmtRON = (v) => `${new Intl.NumberFormat("ro-RO").format(v)} RON`;
const imgSrc = (path) => `${API}/shop/images/${path}`;

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [modelInput, setModelInput] = useState("");

  const scooters = items.filter((p) => (p.category || "trotineta") === "trotineta");
  const toggleModel = (name) => {
    const sel = form.compatible_models.includes(name);
    setForm({ ...form, compatible_models: sel ? form.compatible_models.filter((m) => m !== name) : [...form.compatible_models, name] });
  };
  const addManualModel = () => {
    const v = modelInput.trim();
    if (!v) return;
    if (!form.compatible_models.includes(v)) setForm({ ...form, compatible_models: [...form.compatible_models, v] });
    setModelInput("");
  };

  const loadReviews = async () => setReviews((await api.get("/admin/reviews")).data);

  const load = async () => setItems((await api.get("/admin/products")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, price: p.price, old_price: p.old_price || "", stock: p.stock, category: p.category || "trotineta", short_desc: p.short_desc || "", description: p.description || "", specs: p.specs || {}, featured: p.featured, active: p.active, compatible_models: p.compatible_models || [] });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast.error("Completeaza numele si pretul"); return; }
    const payload = {
      ...form,
      price: parseFloat(form.price),
      old_price: form.old_price ? parseFloat(form.old_price) : 0,
      stock: parseInt(form.stock || 0, 10),
    };
    try {
      if (editing) {
        await api.patch(`/admin/products/${editing.id}`, payload);
        toast.success("Produs actualizat");
      } else {
        const r = await api.post("/admin/products", payload);
        toast.success("Produs creat! Acum adauga poze.");
        setEditing(r.data);
        load();
        return;
      }
      setOpen(false);
      load();
    } catch { toast.error("Eroare la salvare"); }
  };

  const uploadPhotos = async (files) => {
    if (!editing) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/admin/products/${editing.id}/photos`, fd);
      }
      toast.success("Poze incarcate");
      const r = await api.get("/admin/products");
      setItems(r.data);
      setEditing(r.data.find((p) => p.id === editing.id));
    } catch { toast.error("Eroare la incarcare"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deletePhoto = async (photoId) => {
    await api.delete(`/admin/products/${editing.id}/photos/${photoId}`);
    const r = await api.get("/admin/products");
    setItems(r.data);
    setEditing(r.data.find((p) => p.id === editing.id));
  };

  const remove = async (p) => {
    if (!confirm(`Sterge produsul ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Produs sters");
    load();
  };

  const toggle = async (p, field) => {
    await api.patch(`/admin/products/${p.id}`, { [field]: !p[field] });
    load();
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl uppercase">Produse</h1>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">Trotinetele din magazinul Troti-X</div>
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="reviews-manager-btn" onClick={() => { setReviewsOpen(true); loadReviews(); }}
            className="bg-neutral-900 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-neutral-800 rounded-none uppercase tracking-wider font-bold text-xs">
            <MessageSquare className="w-4 h-4 mr-2" /> Recenzii
          </Button>
          <Button data-testid="new-product-btn" onClick={openNew} className="bg-[#00E5FF] text-black hover:bg-[#5CEEFF] rounded-none uppercase tracking-wider font-bold">
            <Plus className="w-4 h-4 mr-2" /> Produs nou
          </Button>
        </div>
      </div>

      {items.length === 0 && (
        <div className="border border-dashed border-neutral-800 p-16 text-center text-neutral-500">
          <Bike className="w-10 h-10 mx-auto mb-3 text-neutral-700" />
          Niciun produs. Adauga prima trotineta in magazin!
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className={`border bg-neutral-950 ${p.active ? "border-neutral-800" : "border-red-900/50 opacity-60"}`}>
            <div className="flex gap-4 p-4">
              <div className="w-24 h-24 bg-black border border-neutral-900 flex-shrink-0 overflow-hidden">
                {(p.images || [])[0] ? <img src={imgSrc(p.images[0].path)} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-neutral-800"><Zap className="w-8 h-8" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm flex items-center gap-2">
                  {p.featured && <Star className="w-3.5 h-3.5 text-[#00E5FF]" fill="currentColor" />}
                  <span className="truncate">{p.name}</span>
                </div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">{CATEGORIES[p.category] || "Trotinetă"}</div>
                <div className="text-[#00E5FF] font-bold mt-1">{fmtRON(p.price)}</div>
                <div className={`text-xs mt-1 ${p.stock > 2 ? "text-green-400" : p.stock > 0 ? "text-amber-400" : "text-red-400"}`}>
                  Stoc: {p.stock}
                  {p.stock <= 2 && (
                    <span data-testid={`low-stock-badge-${p.id}`} className="inline-flex items-center gap-1 ml-2 font-bold uppercase text-[9px] tracking-wider">
                      <AlertTriangle className="w-3 h-3" /> {p.stock === 0 ? "Epuizat!" : "Stoc redus!"}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-neutral-600 mt-0.5">{(p.images || []).length} poze</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 pb-3 border-t border-neutral-900 pt-3">
              <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-400">
                <Switch data-testid={`product-active-${p.id}`} checked={p.active} onCheckedChange={() => toggle(p, "active")} /> Activ
              </label>
              <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-400 ml-2">
                <Switch data-testid={`product-featured-${p.id}`} checked={p.featured} onCheckedChange={() => toggle(p, "featured")} /> Top
              </label>
              <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="ml-auto p-2 text-neutral-400 hover:text-[#00E5FF]"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} className="p-2 text-neutral-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={reviewsOpen} onOpenChange={setReviewsOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#00E5FF]" /> Recenzii clienti</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {reviews.length === 0 && <div className="text-center text-neutral-500 text-sm py-6">Nicio recenzie inca.</div>}
            {reviews.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-neutral-800 p-3">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">{r.name}</span>
                  <span className="inline-flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "text-[#00E5FF]" : "text-neutral-700"}`} fill={i <= r.rating ? "currentColor" : "none"} />
                    ))}
                  </span>
                  <button data-testid={`delete-review-${r.id}`} onClick={async () => { if (confirm("Stergi recenzia?")) { await api.delete(`/admin/reviews/${r.id}`); loadReviews(); load(); } }}
                    className="ml-auto p-1.5 text-neutral-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#00E5FF] mt-1">{r.product_name}</div>
                {r.comment && <p className="text-sm text-neutral-400 mt-1.5">{r.comment}</p>}
                <div className="text-[10px] text-neutral-600 mt-1">{new Date(r.created_at).toLocaleString("ro-RO")}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{editing ? `Editeaza: ${editing.name}` : "Produs nou"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Nume produs *</Label>
                <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Categorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category-select" className="bg-black border-neutral-800 rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                    <SelectItem value="trotineta">Trotinetă</SelectItem>
                    <SelectItem value="piesa">Piesă</SelectItem>
                    <SelectItem value="accesoriu">Accesoriu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Pret (RON) *</Label>
                <Input data-testid="product-price-input" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Pret vechi (taiat)</Label>
                <Input data-testid="product-oldprice-input" type="number" step="0.01" value={form.old_price} onChange={(e) => setForm({ ...form, old_price: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Stoc</Label>
                <Input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Descriere scurta (apare pe card)</Label>
              <Input data-testid="product-shortdesc-input" value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Descriere completa</Label>
              <Textarea data-testid="product-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-black border-neutral-800 rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#00E5FF]">Specificatii</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {SPECS.map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</Label>
                    <Input data-testid={`product-spec-${k}`} value={form.specs[k] || ""} onChange={(e) => setForm({ ...form, specs: { ...form.specs, [k]: e.target.value } })} className="bg-black border-neutral-800 rounded-none mt-1 h-9 text-sm" />
                  </div>
                ))}
              </div>
            </div>
            {form.category !== "trotineta" && (
              <div data-testid="compat-section-admin" className="border border-[#00E5FF]/20 p-4">
                <Label className="text-xs uppercase tracking-wider text-[#00E5FF]">Compatibil cu (modele trotinete)</Label>
                <div className="text-[10px] text-neutral-500 mt-1">Selecteaza trotinetele din magazin pe care se potriveste piesa, sau adauga manual alte modele.</div>
                {scooters.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {scooters.map((s) => {
                      const sel = form.compatible_models.includes(s.name);
                      return (
                        <button key={s.id} type="button" data-testid={`compat-toggle-${s.id}`} onClick={() => toggleModel(s.name)}
                          className={`px-3 py-1.5 text-xs border transition-colors ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"}`}>
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Input data-testid="compat-manual-input" value={modelInput} onChange={(e) => setModelInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManualModel(); } }}
                    placeholder="Alt model (ex: KuKirin G2 Pro)" className="bg-black border-neutral-800 rounded-none h-9 text-sm" />
                  <Button type="button" data-testid="compat-add-btn" onClick={addManualModel}
                    className="bg-neutral-900 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-neutral-800 rounded-none h-9 text-xs uppercase tracking-wider flex-shrink-0">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Adauga
                  </Button>
                </div>
                {form.compatible_models.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {form.compatible_models.map((m) => (
                      <span key={m} data-testid={`compat-chip-${m}`} className="inline-flex items-center gap-1.5 bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] text-xs px-2 py-1">
                        {m}
                        <button type="button" onClick={() => toggleModel(m)} className="hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-neutral-300">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /> Produs Top (evidentiat)
              </label>
              <label className="flex items-center gap-2 text-sm text-neutral-300">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Activ pe site
              </label>
            </div>

            {editing && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#00E5FF]">Poze produs</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(editing.images || []).map((im) => (
                    <div key={im.id} className="relative w-20 h-20 border border-neutral-800 group">
                      <img src={imgSrc(im.path)} alt="" className="w-full h-full object-cover" />
                      <button type="button" data-testid={`delete-photo-${im.id}`} onClick={() => deletePhoto(im.id)}
                        className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button type="button" data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="w-20 h-20 border border-dashed border-[#00E5FF]/40 flex flex-col items-center justify-center text-[#00E5FF] hover:bg-[#00E5FF]/5 transition-colors">
                    <ImagePlus className="w-5 h-5" />
                    <span className="text-[9px] uppercase mt-1">{uploading ? "..." : "Adauga"}</span>
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => uploadPhotos([...e.target.files])} />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button data-testid="product-save-btn" type="submit" className="bg-[#00E5FF] text-black hover:bg-[#5CEEFF] rounded-none uppercase tracking-wider font-bold w-full">
                {editing ? "Salveaza modificarile" : "Creeaza produsul"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
